
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

export default function BrandingSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredService, setHoveredService] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      title: 'Branding',
      description: 'Developing your brand\'s core essence, values, and strategic positioning to create a cohesive and impactful identity that stands out.',
      icon: 'ri-heart-line'
    },
    {
      title: 'Logo Design',
      description: 'Crafting unique, memorable logos that instantly communicate your brand\'s personality, purpose, and visual appeal.',
      icon: 'ri-brush-line'
    },
    {
      title: 'Packaging Design',
      description: 'Designing captivating packaging that not only protects your product but also tells its story and ensures it shines on any shelf.',
      icon: 'ri-gift-line'
    },
    {
      title: 'Campaign Identity',
      description: 'Creating distinctive visual and thematic identities for your marketing campaigns that ensure consistency, memorability, and strong audience recall.',
      icon: 'ri-megaphone-line'
    },
    {
      title: 'Graphic & Motion Design',
      description: 'Bringing your brand to life through stunning static visuals, engaging animations, and dynamic video content across all platforms.',
      icon: 'ri-movie-line'
    }
  ];

  const handleBrandingClick = () => {
    navigate('/services/branding');
  };

  return (
    <section 
      ref={sectionRef}
      id="branding"
      className="py-20 bg-gradient-to-b from-black to-gray-900 relative overflow-hidden"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `url('/assets/branding-bg.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/60"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <div className={`transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
          }`}>
            <div className="flex items-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center mr-4">
                <i className="ri-palette-line text-2xl text-white"></i>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white">
                Crafting Brands That 
                <span className="bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent"> Roar</span>
              </h2>
            </div>
            
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Your brand is more than just a logo; it's your story, your promise, and your unique fingerprint 
              in the market. We specialize in building compelling brand identities that resonate deeply with 
              your audience, communicate your essence, and stand the test of time, ensuring you make an 
              unforgettable impression.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <button 
                onClick={handleBrandingClick}
                className="group bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white px-6 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <span className="flex items-center gap-2">
                  Explore Branding & Visual Identity
                  <i className="ri-external-link-line group-hover:rotate-45 transition-transform duration-300"></i>
                </span>
              </button>
              
              <button className="group border-2 border-orange-500 hover:bg-orange-500 text-orange-500 hover:text-white px-6 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer">
                <span className="flex items-center gap-2">
                  Request Consultation
                  <i className="ri-phone-line group-hover:rotate-12 transition-transform duration-300"></i>
                </span>
              </button>
            </div>
          </div>

          {/* Services List */}
          <div className={`transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
          }`}>
            <div className="space-y-6">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="group bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-orange-500/20 hover:border-orange-500/50 transition-all duration-500 hover:transform hover:scale-105 cursor-pointer"
                  onMouseEnter={() => setHoveredService(index)}
                  onMouseLeave={() => setHoveredService(null)}
                  onClick={handleBrandingClick}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center group-hover:animate-pulse flex-shrink-0">
                      <i className={`${service.icon} text-xl text-white`}></i>
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-2 group-hover:text-orange-400 transition-colors duration-300">
                        {service.title}
                      </h3>
                      <p className="text-gray-300 group-hover:text-white transition-colors duration-300 leading-relaxed">
                        {service.description}
                      </p>
                    </div>

                    <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-2 group-hover:translate-x-0">
                      <i className="ri-arrow-right-line text-orange-400 text-xl"></i>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
